import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Grid, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useStyles } from "./Clusters.styles";
import { ModalClusters } from "./components/ModalClusters/ModalClusters.view";
import useRolesPermissions from "hooks/useRolesPermissions";
import { ClustersCreationForm } from "./forms/ClustersCreationForm";
import { ClusterEntity } from "entities/ClusterEntity/ClusterEntity";
import { ClusterCard } from "./components/ClusterCard/ClusterCard.view";
import { ICluster } from "entities/ClusterEntity/ClusterEntityInterfaces";

export const Clusters = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const routeParams = useParams();
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const millId = useMemo(() => Number(routeParams?.millId) || 0, [routeParams?.millId]);
    const { clusters, refetch } = ClusterEntity.GetClusters(millId);
    const [editableCluster, setEditableCluster] = useState<ICluster | null>(null);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = useCallback(() => {
        refetch();
        setOpen(false);
        setEditableCluster(null);
    }, [refetch, setOpen]);

    return (
        <div className={classes.container}>
            {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters && (
                <div className={classes.buttonContainer}>
                    <Button
                        disabled={!CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters}
                        className={classes.addClusterButton}
                        variant="text"
                        onClick={handleOpen}
                    >
                        <AddCircleIcon />
                        <Typography variant="h5" className={classes.addClusterButtonText}>
                            {t("corporate_backoffice.cluster_view.add_cluster_button")}
                        </Typography>
                    </Button>
                    <div className={classes.horizontalLine} />
                </div>
            )}
            <Grid container className={classes.cardContainer}>
                {clusters.length < 1 && (
                    <Grid item xs={12} className={classes.noClusterMessage}>
                        <Typography variant="subtitle1">
                            {t("corporate_backoffice.cluster_view.no_clusters")}
                        </Typography>
                    </Grid>
                )}
                {clusters.map((cluster) => (
                    <Grid item xs={2.9} key={cluster.cluster_id}>
                        <ClusterCard
                            cluster={cluster}
                            handleOpen={handleOpen}
                            setEditableCluster={setEditableCluster}
                        />
                    </Grid>
                ))}
            </Grid>
            <ModalClusters
                open={open}
                handleClose={handleClose}
                title={
                    editableCluster
                        ? t("corporate_backoffice.cluster_view.cluster_creation_form.modal_edit_cluster_title")
                        : t("corporate_backoffice.cluster_view.cluster_creation_form.modal_create_cluster_title")
                }
            >
                <ClustersCreationForm
                    handleClose={handleClose}
                    millId={millId}
                    cluster={editableCluster}
                    clustersRefetch={refetch}
                />
            </ModalClusters>
        </div>
    );
};
